/**
 * What each metric on the quality page measures — the single copy of that wording.
 *
 * The failure mode of a metrics page is not a wrong number but a right number under a label
 * meaning something else, so the tooltips read from here rather than carrying their own
 * strings. Client-safe because the query layer is `server-only`.
 *
 * Two kinds of metric sit on this page and must never be described as one:
 *
 * - **phase-attributed** — per actor inside a session, so a session both sides handled
 *   contributes its AI phase to AI and its human phase to Team.
 * - **cohort** — per session, and the whole session belongs to whoever handled it.
 */

import {
  RESOLVED_OUTCOMES,
  REQUEST_OUTCOMES,
  SESSION_OUTCOMES,
  type Cohort,
  type CohortOutcomeCounts,
  type OutcomeCounts,
} from "./outcomes"
import { createTranslator, type useTranslations } from "next-intl"
import englishMessages from "@/translations/en/quality-metrics.json"

const englishTranslator = createTranslator({ locale: "en", messages: englishMessages, namespace: "QualityMetrics" })


/** Nothing measured yet reads as «—», never as a zero the operator would trust. */
export const NO_VALUE = "—"

/** What the page measures over, said in the page's own words. */
export const SERVICE_METRIC_POPULATION = getSERVICE_METRIC_POPULATION()

export function getSERVICE_METRIC_POPULATION(t: ReturnType<typeof useTranslations<"QualityMetrics">> = englishTranslator) {
  return t("finalizedSessionsThatReceivedAtLeastOneCustomer")
}

/** The denominator both rates divide by. */
export const AI_HANDLED_POPULATION = getAI_HANDLED_POPULATION()

export function getAI_HANDLED_POPULATION(t: ReturnType<typeof useTranslations<"QualityMetrics">> = englishTranslator) {
  return t("aIAnsweredSessions")
}

export const ACTOR_LABELS = getACTOR_LABELS()

export function getACTOR_LABELS(t: ReturnType<typeof useTranslations<"QualityMetrics">> = englishTranslator) {
  return { ai: t("aI"), team: t("yourTeam") } as const
}

export const COHORT_LABELS = getCOHORT_LABELS()

export function getCOHORT_LABELS(t: ReturnType<typeof useTranslations<"QualityMetrics">> = englishTranslator): Record<Cohort, string> {
  return {
  aiOnly: t("theAgentAlone"),
  teamInvolved: t("yourTeamTookPart"),
  unattended: t("nobodyAnswered"),
}
}

/** What each cohort holds, for the split that names them. */
export const COHORT_DEFINITIONS = getCOHORT_DEFINITIONS()

export function getCOHORT_DEFINITIONS(t: ReturnType<typeof useTranslations<"QualityMetrics">> = englishTranslator): Record<Cohort, string> {
  return {
  aiOnly: t("theAgentCarriedTheRequestFromTheFirst"),
  teamInvolved: t("somebodyFromYourTeamWroteAtSomePoint"),
  unattended: t("neitherTheAgentNorYourTeamWroteAt"),
}
}

/** How each half is attributed — the captions the two comparison groups carry. */
export const ATTRIBUTION_CAPTIONS = getATTRIBUTION_CAPTIONS()

export function getATTRIBUTION_CAPTIONS(t: ReturnType<typeof useTranslations<"QualityMetrics">> = englishTranslator) {
  return {
  phase: t("perPhaseASessionBothSidesHandledCounts"),
  cohort: t("perSessionTheWholeSessionBelongsToWhoever"),
} as const
}

export type ServiceMetricKey =
  | "firstResponse"
  | "replyTime"
  | "handleTime"
  | "customerEffort"
  | "transferRate"
  | "escalationRate"
  | "operatorTakeovers"
  | "pickedUp"
  | "escalationsHandled"
  | "timeToHuman"
  | "answeredByEmail"

export const SERVICE_METRIC_DEFINITIONS = getSERVICE_METRIC_DEFINITIONS()

export function getSERVICE_METRIC_DEFINITIONS(t: ReturnType<typeof useTranslations<"QualityMetrics">> = englishTranslator): Record<
  ServiceMetricKey,
  { label: string; definition: string; /** Which way is the better half, where the number alone does not say. */ direction?: string }
> {
  return {
  firstResponse: {
    label: t("firstResponseMedian"),
    definition:
      t("medianWaitForTheFirstReplyFromThat"),
  },
  replyTime: {
    label: t("replyTimeMedian"),
    definition:
      t("medianOfEachSessionSAverageWaitFor"),
  },
  handleTime: {
    label: t("handlingTimeMedian"),
    definition:
      t("medianTimeFromTheCustomerSFirstMessage"),
  },
  customerEffort: {
    label: t("customerMessagesPerSessionAverage"),
    definition: t("howManyMessagesACustomerHadToSend"),
    direction: "fewer means the customer got there with less work",
  },
  transferRate: {
    label: t("transferRate"),
    definition:
      t("aIAnsweredSessionsThatReachedAPersonThe"),
  },
  escalationRate: {
    label: t("escalationRate"),
    definition: t("aIAnsweredSessionsWhereTheAgentItselfAsked"),
  },
  operatorTakeovers: {
    label: t("operatorTakeovers"),
    definition: t("sessionsTheAgentWasAnsweringWhenAPerson"),
  },
  pickedUp: {
    label: t("escalationsPickedUp"),
    definition: t("escalatedSessionsAPersonRepliedToInThe"),
  },
  escalationsHandled: {
    label: t("escalationsHandled"),
    definition: t("handoversThisPersonAnswered"),
  },
  timeToHuman: {
    label: t("timeToFirstHumanReply"),
    definition: t("medianWaitFromTheHandoverToTheTeam"),
  },
  answeredByEmail: {
    label: t("byEmail"),
    definition:
      t("yourMailboxRepliedInTheCustomerSEmail"),
  },
}
}

/** A share of a population, or null when the population is empty — never a misleading zero. */
export function share(numerator: number, denominator: number): number | null {
  return denominator > 0 ? numerator / denominator : null
}

/** How many of how many a share is — what its tile says beside the percentage. */
export interface ShareCounts {
  of: number;
  over: number;
}

/** Below this a question is an anecdote, not a pattern worth teaching the agent. */
export const UNRESOLVED_TOPIC_MIN_SESSIONS = 3

/** Below this in a window, a session or two would swing a share: too few to compare it with another. */
export const MIN_SHARE_POPULATION = 5

export type OutcomeMetricKey =
  | "selfServiceShare"
  | "resolutionRate"
  | "outcomes"
  | "byCohort"
  | "handoverOutcomes"
  | "escalationCategories"
  | "takeoverReasons"
  | "needsReview"
  | "unresolvedTopics"

export const OUTCOME_METRIC_DEFINITIONS = getOUTCOME_METRIC_DEFINITIONS()

export function getOUTCOME_METRIC_DEFINITIONS(t: ReturnType<typeof useTranslations<"QualityMetrics">> = englishTranslator): Record<
  OutcomeMetricKey,
  { label: string; definition: string }
> {
  return {
  selfServiceShare: {
    label: t("resolvedByTheAgent"),
    definition: t("realRequestsTheAgentResolvedOnItsOwn"),
  },
  resolutionRate: {
    label: t("leftToTheAgent"),
    definition:
      t("resolvedRequestsOutOfThoseLeftToThe"),
  },
  outcomes: {
    label: t("howSessionsEnded"),
    definition:
      t("everyJudgedRealRequestByItsOutcomeStory"),
  },
  byCohort: {
    label: t("whoHandledIt"),
    definition:
      t("everyRequestByWhoCarriedItTheAgent"),
  },
  handoverOutcomes: {
    label: t("whatHappenedAfter"),
    definition: t("howYourTeamSPartEndedForSessions"),
  },
  escalationCategories: {
    label: t("whyTheAgentHandedOver"),
    definition: t("sessionsHandedToYourTeamByTheReason"),
  },
  takeoverReasons: {
    label: t("whyYourTeamSteppedIn"),
    definition: t("sessionsAPersonJoinedWithoutTheAgentAsking"),
  },
  needsReview: {
    label: t("reviewQueue"),
    definition:
      t("verdictsWaitingForAPersonTheReviewWas"),
  },
  unresolvedTopics: {
    label: t("questionsTheAgentDidnTResolve"),
    definition:
      t("requestsTheAgentDidNotResolveAloneThose"),
  },
}
}

/** Every cohort added back together — how sessions ended, whoever handled them. */
export function totalOutcomes(split: CohortOutcomeCounts): OutcomeCounts {
  return Object.fromEntries(
    SESSION_OUTCOMES.map((outcome) => [
      outcome,
      split.aiOnly[outcome] + split.teamInvolved[outcome] + split.unattended[outcome],
    ])
  ) as OutcomeCounts
}

/** Every judged session that was a request at all — the population both outcome rates share. */
export function requestCount(counts: OutcomeCounts): number {
  return REQUEST_OUTCOMES.reduce((sum, outcome) => sum + counts[outcome], 0)
}

/**
 * Resolved by the agent alone, out of every real request. A resolved verdict on a session the
 * team took part in is the team's success, whoever gave the verdict.
 */
export function selfServiceShare(split: CohortOutcomeCounts): number | null {
  return shareOf(selfServiceCounts(split))
}

export function selfServiceCounts(split: CohortOutcomeCounts): ShareCounts {
  return { of: resolvedCount(split.aiOnly), over: requestCount(totalOutcomes(split)) }
}

/** The same sessions, out of the requests left to the agent: no team, and not handed over. */
export function resolutionRate(split: CohortOutcomeCounts): number | null {
  return shareOf(resolutionCounts(split))
}

export function resolutionCounts(split: CohortOutcomeCounts): ShareCounts {
  const leftToAgent =
    requestCount(split.aiOnly) -
    split.aiOnly.escalated +
    requestCount(split.unattended) -
    split.unattended.escalated
  return { of: resolvedCount(split.aiOnly), over: leftToAgent }
}

/** Handed to team, out of every real request — the funnel's own segment. */
export function handedToTeamCounts(split: CohortOutcomeCounts): ShareCounts {
  const totals = totalOutcomes(split)
  return { of: totals.escalated, over: requestCount(totals) }
}

export function shareOf({ of, over }: ShareCounts): number | null {
  return share(of, over)
}

function resolvedCount(counts: OutcomeCounts): number {
  return RESOLVED_OUTCOMES.reduce((sum, outcome) => sum + counts[outcome], 0)
}
